import { useMemo } from "react"
import {
  ComposedChart,
  Line,
  Scatter,
  Legend,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts"
import { Text } from "@/components"
import { CapTableFunding, CompanyEmployeeCount, EmployeeCountData } from "@/types"
import { Box, Collapsible, HStack, SimpleGrid, Stack, Table } from "@chakra-ui/react"

import { filterBeforeFoundingDate } from "./chartUtils"

interface Props {
  salesCount: EmployeeCountData[]
  engineeringCount: EmployeeCountData[]
  employeeCounts: CompanyEmployeeCount[]
  capTableFunding?: CapTableFunding[]
  foundingDate?: string | null
}

// ── Quarterly aggregation helpers ───────────────────────────────────────────

function toQuarterLabel(dateStr: string): string {
  const d = new Date(dateStr)
  const q = Math.ceil((d.getMonth() + 1) / 3)
  return `${d.getFullYear()}-Q${q}`
}

type QuarterData = {
  label: string
  salesCount: number
  engCount: number
  totalCount: number
  raisedThisQ: boolean
}

function aggregateToQuarters(
  salesCount: EmployeeCountData[],
  engineeringCount: EmployeeCountData[],
  employeeCounts: CompanyEmployeeCount[],
  capTableFunding: CapTableFunding[]
): QuarterData[] {
  // Build a set of quarters that had fundraising
  const fundingQuarters = new Set<string>()
  capTableFunding.forEach((f) => {
    if (f.issueDate) fundingQuarters.add(toQuarterLabel(f.issueDate))
  })

  // Group by quarter, taking the last data point per quarter
  const salesByQ = new Map<string, number>()
  const sortedSales = [...salesCount].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  sortedSales.forEach((d) => salesByQ.set(toQuarterLabel(d.date), d.count))

  const engByQ = new Map<string, number>()
  const sortedEng = [...engineeringCount].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  sortedEng.forEach((d) => engByQ.set(toQuarterLabel(d.date), d.count))

  const totalByQ = new Map<string, number>()
  const sortedTotal = [...employeeCounts].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  sortedTotal.forEach((d) => totalByQ.set(toQuarterLabel(d.date), d.count))

  // Collect all quarters that have at least sales or eng data
  const allQuarters = new Set<string>()
  salesByQ.forEach((_, k) => allQuarters.add(k))
  engByQ.forEach((_, k) => allQuarters.add(k))

  const sorted = Array.from(allQuarters).sort()

  return sorted
    .filter((q) => salesByQ.has(q) && engByQ.has(q))
    .map((q) => ({
      label: q,
      salesCount: salesByQ.get(q) ?? 0,
      engCount: engByQ.get(q) ?? 0,
      totalCount: totalByQ.get(q) ?? 0,
      raisedThisQ: fundingQuarters.has(q),
    }))
}

// ── Gate evaluation ─────────────────────────────────────────────────────────

type GateResult = {
  label: string
  salesGrowthPct: number | null
  engGrowthPct: number | null
  salesAbsChange: number
  g1: boolean
  g2: boolean
  g3: boolean
  g4: boolean
  g5: boolean
  g6: boolean
  signal: boolean
  isFalsePositive: boolean
  fpReason: string | null
}

function evaluateGates(quarters: QuarterData[]): GateResult[] {
  const results: GateResult[] = []

  for (let i = 0; i < quarters.length; i++) {
    const curr = quarters[i]
    const prev = i > 0 ? quarters[i - 1] : null
    const twoBack = i > 1 ? quarters[i - 2] : null
    const fourBack = i >= 4 ? quarters[i - 4] : null

    // QoQ growth rates — used for gate evaluation
    const salesGrowthQoQ = prev && prev.salesCount > 0
      ? (curr.salesCount - prev.salesCount) / prev.salesCount
      : null
    const engGrowthQoQ = prev && prev.engCount > 0
      ? (curr.engCount - prev.engCount) / prev.engCount
      : null
    const totalGrowthQoQ = prev && prev.totalCount > 0
      ? (curr.totalCount - prev.totalCount) / prev.totalCount
      : null
    const salesAbsChange = prev ? curr.salesCount - prev.salesCount : 0

    // Trailing 2Q sales growth for G1
    const salesGrowth2Q = twoBack && twoBack.salesCount > 0
      ? (curr.salesCount - twoBack.salesCount) / twoBack.salesCount
      : null

    // Trailing 1-year (4Q) growth rates — used for chart trend lines only
    const salesGrowthYoY = fourBack && fourBack.salesCount > 0
      ? (curr.salesCount - fourBack.salesCount) / fourBack.salesCount
      : null
    const engGrowthYoY = fourBack && fourBack.engCount > 0
      ? (curr.engCount - fourBack.engCount) / fourBack.engCount
      : null

    // G1: sales growth >= 25% trailing 2Q AND >= +5 absolute heads
    const g1RatePass = salesGrowth2Q !== null && salesGrowth2Q >= 0.25
    const g1AbsPass = salesAbsChange >= 5
    const g1 = g1RatePass && g1AbsPass

    // G2: eng growth >= -5% (no significant shrinkage)
    const g2 = engGrowthQoQ === null || engGrowthQoQ >= -0.05

    // G3: sales growth rate >= 1.5x eng growth rate
    const g3 = salesGrowthQoQ === null || engGrowthQoQ === null
      ? false
      : salesGrowthQoQ >= 1.5 * engGrowthQoQ

    // G4: total headcount flat or growing
    const g4 = totalGrowthQoQ === null || totalGrowthQoQ >= 0

    // G5: G1-G4 passed for 2 consecutive quarters (check previous result)
    const prevResult = results.length > 0 ? results[results.length - 1] : null
    const prevG1G4 = prevResult ? prevResult.g1 && prevResult.g2 && prevResult.g3 && prevResult.g4 : false
    const currG1G4 = g1 && g2 && g3 && g4
    const g5 = currG1G4 && prevG1G4

    // G6: no fundraise in current or previous quarter
    const raisedRecently = curr.raisedThisQ || (prev?.raisedThisQ ?? false)
    const g6 = !raisedRecently

    const signal = g1 && g2 && g3 && g4 && g5 && g6

    // False positive detection: the naive ratio would fire (sales growing faster than eng)
    // but one or more gates blocked it
    let isFalsePositive = false
    let fpReason: string | null = null

    if (!signal && salesGrowthQoQ !== null && engGrowthQoQ !== null && salesGrowthQoQ > engGrowthQoQ) {
      // Naive ratio fires — check which gate blocked
      if (!g2) {
        isFalsePositive = true
        fpReason = "Layoff artifact"
      } else if (!g4) {
        isFalsePositive = true
        fpReason = "Shrinking company"
      } else if (!g1AbsPass && g1RatePass) {
        isFalsePositive = true
        fpReason = "Small-sample noise"
      } else if (currG1G4 && !g5) {
        isFalsePositive = true
        fpReason = "One-quarter spike"
      } else if (currG1G4 && g5 && !g6) {
        isFalsePositive = true
        fpReason = "Post-raise spree"
      }
    }

    results.push({
      label: curr.label,
      // Chart lines use YoY for smooth trend visualization
      salesGrowthPct: salesGrowthYoY !== null ? salesGrowthYoY * 100 : null,
      engGrowthPct: engGrowthYoY !== null ? engGrowthYoY * 100 : null,
      salesAbsChange,
      g1, g2, g3, g4, g5, g6,
      signal,
      isFalsePositive,
      fpReason,
    })
  }

  return results
}

// ── Signal band computation ─────────────────────────────────────────────────

function computeSignalBands(results: GateResult[]): { start: string; end: string }[] {
  const bands: { start: string; end: string }[] = []
  let bandStart: string | null = null

  for (let i = 0; i < results.length; i++) {
    if (results[i].signal && !bandStart) {
      bandStart = results[i].label
    }
    if (!results[i].signal && bandStart) {
      bands.push({ start: bandStart, end: results[i - 1].label })
      bandStart = null
    }
  }
  if (bandStart) {
    bands.push({ start: bandStart, end: results[results.length - 1].label })
  }

  return bands
}

// ── Custom tooltip ──────────────────────────────────────────────────────────

const PmfTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  const point = payload[0]?.payload
  return (
    <Box bg="card.bg" p={2} border="1px" borderColor="border.muted" borderRadius={6} boxShadow="sm">
      <Text fontSize="xs" fontWeight="bold" mb={1}>{label}</Text>
      {point?.salesGrowthPct != null && (
        <Text fontSize="xs" color="#3182CE">Sales YoY: {point.salesGrowthPct.toFixed(1)}%</Text>
      )}
      {point?.engGrowthPct != null && (
        <Text fontSize="xs" color="#D69E2E">Eng YoY: {point.engGrowthPct.toFixed(1)}%</Text>
      )}
      {point?.signal && (
        <Text fontSize="xs" color="green.600" fontWeight="bold" mt={1}>PMF Signal Active</Text>
      )}
      {point?.isFalsePositive && (
        <Text fontSize="xs" color="red.500" fontWeight="bold" mt={1}>FP: {point.fpReason}</Text>
      )}
    </Box>
  )
}

// ── Component ───────────────────────────────────────────────────────────────

export const PmfCrossoverChart = ({ salesCount, engineeringCount, employeeCounts, capTableFunding = [], foundingDate }: Props) => {
  const quarterlyData = useMemo(
    () => aggregateToQuarters(
      filterBeforeFoundingDate(salesCount, foundingDate),
      filterBeforeFoundingDate(engineeringCount, foundingDate),
      filterBeforeFoundingDate(employeeCounts, foundingDate),
      capTableFunding
    ),
    [salesCount, engineeringCount, employeeCounts, capTableFunding, foundingDate]
  )

  const gateResults = useMemo(() => evaluateGates(quarterlyData), [quarterlyData])

  const signalBands = useMemo(() => computeSignalBands(gateResults), [gateResults])

  // Chart data: all quarters with growth rates
  const chartData = useMemo(
    () => gateResults.map((r) => ({
      label: r.label,
      salesGrowthPct: r.salesGrowthPct,
      engGrowthPct: r.engGrowthPct,
      signal: r.signal,
      isFalsePositive: r.isFalsePositive,
      fpReason: r.fpReason,
      // Scatter data: only show dot on FP quarters
      fpDot: r.isFalsePositive ? r.salesGrowthPct : undefined,
    })),
    [gateResults]
  )

  // Fundraising markers snapped to quarter labels
  const fundraisingMarkers = useMemo(() => {
    const quarterLabels = chartData.map((d) => d.label)
    if (!quarterLabels.length) return []
    return capTableFunding
      .filter((f) => f.issueDate && f.label)
      .map((f) => {
        const targetQ = toQuarterLabel(f.issueDate!)
        // Find exact or nearest quarter label
        let closest = quarterLabels[0]
        for (const q of quarterLabels) {
          if (q === targetQ) { closest = q; break }
          if (q <= targetQ) closest = q
        }
        return { x: closest, label: f.label! }
      })
      .filter((v, i, arr) => arr.findIndex((a) => a.x === v.x) === i)
  }, [capTableFunding, chartData])

  // Summary stats
  const signalQuarters = gateResults.filter((g) => g.signal)
  const fpQuarters = gateResults.filter((g) => g.isFalsePositive)

  if (quarterlyData.length < 3) {
    return (
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={2}>PMF Crossover Signal</Text>
        <Text color="text.muted">Insufficient data for PMF analysis (need at least 3 quarters of sales &amp; engineering headcount).</Text>
      </Box>
    )
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      {/* Header + summary */}
      <HStack mb={2} justifyContent="space-between" alignItems="center">
        <Text fontSize="lg" fontWeight="bold">PMF Crossover Signal</Text>
      </HStack>

      <HStack mb={3} gap={4} flexWrap="wrap">
        <Text fontSize="sm" color={signalQuarters.length > 0 ? "green.600" : "text.muted"} fontWeight={signalQuarters.length > 0 ? "semibold" : "normal"}>
          {signalQuarters.length > 0
            ? `Signal fired: ${signalQuarters[0].label}`
            : "Signal not triggered"}
        </Text>
        {fpQuarters.length > 0 && (
          <Text fontSize="sm" color="orange.500">
            {fpQuarters.length} false positive{fpQuarters.length > 1 ? "s" : ""}
          </Text>
        )}
      </HStack>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData}>
          <XAxis dataKey="label" fontSize={11} />
          <YAxis fontSize={12} unit="%" />
          <Tooltip content={<PmfTooltip />} />
          <Legend />

          {/* Green bands where signal fires */}
          {signalBands.map((band, i) => (
            <ReferenceArea
              key={`sig-${i}`}
              x1={band.start}
              x2={band.end}
              fill="#38A169"
              fillOpacity={0.12}
              label={{ value: "PMF", fontSize: 10, fill: "#38A169" }}
            />
          ))}

          {/* Fundraising markers */}
          {fundraisingMarkers.map((m, i) => (
            <ReferenceLine key={`fr-${i}`} x={m.x} stroke="#A0AEC0" strokeDasharray="4 4" label={{ value: m.label, position: "top", fontSize: 10, fill: "#718096" }} />
          ))}

          {/* Trailing 1-year growth rate lines */}
          <Line type="monotone" dataKey="salesGrowthPct" name="Sales YoY %" stroke="#3182CE" dot={false} connectNulls />
          <Line type="monotone" dataKey="engGrowthPct" name="Eng YoY %" stroke="#D69E2E" dot={false} connectNulls />

          {/* False positive dots */}
          <Scatter dataKey="fpDot" name="False Positive" fill="#E53E3E" shape="circle" />
        </ComposedChart>
      </ResponsiveContainer>

      {/* False positive alerts (collapsible) */}
      {fpQuarters.length > 0 && (
        <Collapsible.Root mt={3}>
          <Collapsible.Trigger cursor="pointer">
            <HStack gap={1}>
              <Text fontSize="xs" fontWeight="bold" color="text.muted">False Positive Alerts ({fpQuarters.length})</Text>
              <Text fontSize="xs" color="text.muted">{"\u25B6"}</Text>
            </HStack>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <Stack mt={1} gap={1}>
              {fpQuarters.map((fp) => (
                <HStack key={fp.label} gap={2}>
                  <Text fontSize="xs" color="red.500" fontWeight="semibold">{fp.label}</Text>
                  <Text fontSize="xs" color="text.muted">{fp.fpReason}</Text>
                </HStack>
              ))}
            </Stack>
          </Collapsible.Content>
        </Collapsible.Root>
      )}

      {/* Gate status table (collapsible) */}
      <Collapsible.Root mt={4}>
        <Collapsible.Trigger cursor="pointer">
          <HStack gap={1}>
            <Text fontSize="xs" fontWeight="bold" color="text.muted">Gate Status by Quarter</Text>
            <Text fontSize="xs" color="text.muted">{"\u25B6"}</Text>
          </HStack>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box mt={2} overflowX="auto">
            <Table.Root size="sm" variant="outline">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader fontSize="xs">Quarter</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">G1</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">G2</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">G3</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">G4</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">G5</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">G6</Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="xs">Result</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {gateResults.slice(1).map((q) => (
                  <Table.Row key={q.label}>
                    <Table.Cell fontSize="xs">{q.label}</Table.Cell>
                    {[q.g1, q.g2, q.g3, q.g4, q.g5, q.g6].map((pass, idx) => (
                      <Table.Cell key={idx} fontSize="xs" color={pass ? "green.500" : "red.400"}>
                        {pass ? "\u2713" : "\u2717"}
                      </Table.Cell>
                    ))}
                    <Table.Cell fontSize="xs" fontWeight="bold" color={q.signal ? "green.600" : q.isFalsePositive ? "orange.500" : "gray.400"}>
                      {q.signal ? "FIRE" : q.fpReason || "\u2014"}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>

      {/* Gate glossary */}
      <Box mt={4}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted" mb={2}>Gate Glossary</Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={2}>
          {[
            { gate: "G1", label: "Sales Traction", desc: "Trailing 2Q sales growth \u2265 25% and \u2265 +5 absolute hires" },
            { gate: "G2", label: "No Eng Layoffs", desc: "Engineering QoQ growth \u2265 -5%" },
            { gate: "G3", label: "Sales Outpacing Eng", desc: "Sales growth \u2265 1.5\u00D7 engineering growth (QoQ)" },
            { gate: "G4", label: "Company Growing", desc: "Total headcount flat or growing QoQ" },
            { gate: "G5", label: "Sustained Signal", desc: "G1\u2013G4 passed for 2 consecutive quarters" },
            { gate: "G6", label: "Not Post-Raise", desc: "No fundraising round in current or prior quarter" },
          ].map((g) => (
            <HStack key={g.gate} gap={2} alignItems="baseline">
              <Text fontSize="xs" fontWeight="bold" color="blue.500" minW="24px">{g.gate}</Text>
              <Text fontSize="xs"><Text as="span" fontWeight="semibold">{g.label}</Text> — {g.desc}</Text>
            </HStack>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  )
}
